// PNR-group Uber-voucher wire shapes (#797 slice 1).
//
// UI MIRROR of `operator-bff:GroupVoucherView@v1`
// (`apps/operator-bff/src/modules/transport/transport-voucher.types.ts`) — a COPY,
// not an import, for the same reason `types.ts` copies `TripView@v1`: the bundle
// must not couple to a Nest app, and this file must not drift from the frozen seam.
//
// `provider` is the #785 seam — constant `UBER` today; render it through the SAME
// `providerLabel()` the trips use, so a future Cabify voucher is a value change.

export type VoucherScenario = 'HOTEL_OVERNIGHT' | 'RIDE_HOME' | 'ONE_WAY';

/**
 * Local lifecycle of the program (vendor code state arrives via webhooks):
 * ISSUING → CODES_PENDING → ACTIVE, CANCELLED terminal.
 */
export type VoucherProgramStatus = 'ISSUING' | 'CODES_PENDING' | 'ACTIVE' | 'CANCELLED';

export interface VoucherGeoFence {
  latitude: number;
  longitude: number;
  radiusMeters: number;
  address: string;
}

/**
 * One passenger ↔ one code. `claimedAt`/`redeemedAt` are WEBHOOK-written (the
 * vendor is the authority on code usage) — the section renders them as badges and
 * never computes them.
 */
export interface GroupVoucherAssignmentView {
  passengerUrn: string;
  codeId: string;
  codeText: string;
  redemptionLink: string;
  claimedAt?: string;
  redeemedAt?: string;
  distributedAt?: string;
  /** Operator-cancelled code (#826): this one code is dead, the program lives on. */
  cancelledAt?: string;
  cancelledReason?: string;
}

export interface GroupVoucherView {
  urn: string;
  provider: string;
  caseUrn: string;
  subCaseUrn: string;
  scenario: VoucherScenario;
  status: VoucherProgramStatus;
  revision: number;
  programId?: string;
  currencyCode: string;
  numberOfCodes: number;
  /** Care window, epoch MILLISECONDS — rendered in the program's own `timezone`. */
  startsAt: number;
  endsAt: number;
  timezone: string;
  airport: VoucherGeoFence;
  destination: VoucherGeoFence;
  maxTripsPerVoucher: number;
  capPerTripUsd?: number;
  passengerUrns: string[];
  assignments: GroupVoucherAssignmentView[];
  vendorStatus?: string;
  lastError?: string;
  cancelledAt?: string;
  cancelledReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** The booked hotel's identity — all this console knows; the domain confirms it against Booking. */
export interface VoucherHotelRef {
  reservationUrn: string;
  hotelUrn: string;
}

/**
 * `operator-bff:POST pnr-group voucher@v1` — `reason` is mandatory on every write.
 * Per side: an explicit fence (wins) OR its reference — `airportIata` resolved by the
 * domain's network table, `hotelRef` confirmed against Booking. This console sends
 * REFERENCES (it has no coordinates); the only fence it ever builds itself is the
 * RIDE_HOME destination, whose point cases already geocoded.
 */
export interface IssueGroupVoucherInput {
  scenario: VoucherScenario;
  /** The case's parent disruption — Uber's reconciliation memo groups spend by it. */
  disruptionUrn?: string;
  airport?: VoucherGeoFence;
  airportIata?: string;
  destination?: VoucherGeoFence;
  hotelRef?: VoucherHotelRef;
  startsAt: number;
  endsAt: number;
  timezone: string;
  currencyCode: string;
  passengerUrns: string[];
  reason: string;
}

export interface ReissueGroupVoucherInput {
  destination: VoucherGeoFence;
  reason: string;
}

/**
 * In-place amendment (M4): the live program keeps its codes and claims. This console
 * sends the booked hotel's REFERENCE (it has no coordinates) and/or the extended
 * care-window end; the BFF refuses an empty patch.
 */
export interface AmendGroupVoucherInput {
  hotelRef?: VoucherHotelRef;
  destination?: VoucherGeoFence;
  /** The NEW care-window end, epoch ms — the domain enforces extend-only. */
  endsAt?: number;
  reason: string;
}

/** GET envelope — `voucher: null` is the "none issued yet" state, not an error. */
export interface GroupVoucherReadResult {
  voucher: GroupVoucherView | null;
}

export interface GroupVoucherResult {
  voucher: GroupVoucherView;
}

/**
 * TanStack Query key for a group's voucher. One key per PNR group — the program IS
 * group-grain, and the WS merge (`TransportVoucherChanged`) writes the whole view
 * under the same key.
 */
export function groupVoucherKey(subCaseUrn: string): readonly unknown[] {
  return ['transport-voucher', subCaseUrn];
}

/**
 * Badge presentation per status — tone is PRESENTATION and lives here; the caption
 * is LANGUAGE and lives in the `pnrActions` bundle (same split as
 * `TRIP_STATUS_PRESENTATION`).
 */
export interface VoucherStatusPresentation {
  labelKey: string;
  tone: 'muted' | 'info' | 'warning' | 'success' | 'error' | 'neutral';
}

export const VOUCHER_STATUS_PRESENTATION: Record<VoucherProgramStatus, VoucherStatusPresentation> = {
  ISSUING: { labelKey: 'transport.voucher.status.issuing', tone: 'muted' },
  CODES_PENDING: { labelKey: 'transport.voucher.status.codesPending', tone: 'info' },
  ACTIVE: { labelKey: 'transport.voucher.status.active', tone: 'success' },
  CANCELLED: { labelKey: 'transport.voucher.status.cancelled', tone: 'neutral' },
};

/** Codes are still being generated — the sync-codes resume hook can finish them now. */
export function isVoucherSyncable(status: VoucherProgramStatus): boolean {
  return status === 'ISSUING' || status === 'CODES_PENDING';
}
